import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Zap, Chrome, MousePointerClick, Shield, Sparkles, ArrowRight, CheckCircle2, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { FormMemoryPanel } from '../components/FormMemoryPanel';
import { StoreButtons, ManualInstallLinks, anyStoreLive } from '../components/StoreButtons';

export default function Extension() {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  const zipUrl = '/lifeautofill-extension.zip';

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.origin + zipUrl);
      setCopied(true);
      toast.success('Link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error('Copy failed');
    }
  };

  return (
    <div className="container mx-auto px-4 md:px-8 py-10 md:py-16 max-w-5xl" data-testid="extension-page">
      {/* Hero */}
      <div className="border-2 border-border p-6 md:p-10 mb-10" style={{ boxShadow: '6px 6px 0 0 hsl(var(--primary))' }}>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-primary mb-4">
          <Sparkles className="w-3.5 h-3.5" /> NEW · v1.4 · AI FILLS ANY FORM + ATTACHES YOUR DOCUMENTS
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-none mb-4">
          ONE CLICK.<br />ANY FORM.<br />DONE.
        </h1>
        <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mb-6">
          Enter your details once. Then every rental, job, gym, loan or government
          application fills in one click: text fields, dropdowns, referees, and even your
          ID and payslip uploads. Multi-step forms keep filling as you click Next.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <StoreButtons />
          <Button variant="outline" size="lg" onClick={copyLink} className="uppercase tracking-widest" data-testid="copy-link-btn">
            {copied ? <CheckCircle2 className="w-4 h-4 mr-2 text-primary" /> : <Copy className="w-4 h-4 mr-2" />}
            {copied ? 'COPIED' : 'COPY LINK'}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-4 font-mono">
          Chrome / Edge / Brave / Firefox 142+{anyStoreLive ? '' : ' · Store listings coming soon'}
        </p>
        <ManualInstallLinks />
      </div>

      {/* Install steps */}
      <div className="mb-12">
        <h2 className="font-heading text-2xl uppercase tracking-wider mb-6">
          <span className="text-primary">→</span> INSTALL IN 4 STEPS
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          {[
            {
              n: '01',
              title: 'DOWNLOAD & UNZIP',
              body: 'Click Download above. Unzip the file to a folder you\'ll keep (e.g. Desktop/lifeautofill-extension).',
            },
            {
              n: '02',
              title: 'OPEN chrome://extensions',
              body: 'Paste chrome://extensions into your address bar. Toggle "Developer mode" ON (top-right).',
            },
            {
              n: '03',
              title: 'LOAD UNPACKED',
              body: 'Click "Load unpacked" (top-left). Select the folder you unzipped. The Life Admin icon appears in your toolbar.',
            },
            {
              n: '04',
              title: 'PIN & SIGN IN HERE',
              body: 'Click the puzzle icon → pin Life Admin. Then visit lifeautofill.com/dashboard. Your session syncs automatically.',
            },
          ].map((step) => (
            <div key={step.n} className="border-2 border-border p-5" data-testid={`step-${step.n}`}>
              <div className="font-mono text-3xl text-primary mb-2">{step.n}</div>
              <div className="font-heading text-sm uppercase tracking-widest mb-2">{step.title}</div>
              <p className="text-sm text-muted-foreground">{step.body}</p>
            </div>
          ))}
        </div>
        <div className="border-2 border-border p-5 mt-4" data-testid="firefox-steps">
          <div className="font-heading text-sm uppercase tracking-widest mb-2">ON FIREFOX?</div>
          <p className="text-sm text-muted-foreground">
            Download the Firefox build → open <span className="font-mono">about:debugging#/runtime/this-firefox</span> →
            {' "Load Temporary Add-on…"'} → pick the .zip. Temporary add-ons are removed when Firefox restarts until our Firefox Add-ons listing is approved.
          </p>
        </div>
      </div>

      {user && <FormMemoryPanel />}

      {/* How it works */}
      <div className="mb-12">
        <h2 className="font-heading text-2xl uppercase tracking-wider mb-6">
          <span className="text-primary">→</span> HOW ONE CLICK FILLS EVERYTHING
        </h2>
        <div className="grid md:grid-cols-4 gap-4 mb-8" data-testid="fill-engine-steps">
          {[
            ['1 · KNOWN FIELDS', '120+ field types (names, addresses, TFN, licence, BSB, weekly rent, referees) fill instantly.'],
            ['2 · AI FOR THE REST', 'Fields it has never seen are worked out by AI from their labels. Your data is never sent to the AI.'],
            ['3 · DOCUMENTS', 'Proof of ID, payslips and resume upload boxes are filled from your document vault.'],
            ['4 · EVERY STEP', 'Click Next and the following step fills automatically, counted as one autofill.'],
          ].map(([t, d]) => (
            <div key={t} className="border-2 border-foreground p-5">
              <div className="font-heading text-xs uppercase tracking-widest text-primary mb-2">{t}</div>
              <p className="text-sm text-muted-foreground">{d}</p>
            </div>
          ))}
        </div>
        <h3 className="font-heading text-sm uppercase tracking-widest mb-4">THREE WAYS TO START</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="border-2 border-border p-5">
            <Zap className="w-6 h-6 text-primary mb-3" />
            <div className="font-heading text-sm uppercase tracking-widest mb-2">FLOATING BUTTON</div>
            <p className="text-sm text-muted-foreground">A small ⚡ Autofill button appears on any page with a form. One click fills every field it can, then highlights anything only you can answer.</p>
          </div>
          <div className="border-2 border-border p-5">
            <MousePointerClick className="w-6 h-6 text-primary mb-3" />
            <div className="font-heading text-sm uppercase tracking-widest mb-2">RIGHT-CLICK</div>
            <p className="text-sm text-muted-foreground">Right-click anywhere on a form → {'"Autofill this page"'}. Or right-click a single input → {'"Fill this field"'}.</p>
          </div>
          <div className="border-2 border-border p-5">
            <Chrome className="w-6 h-6 text-primary mb-3" />
            <div className="font-heading text-sm uppercase tracking-widest mb-2">TOOLBAR POPUP</div>
            <p className="text-sm text-muted-foreground">Click the extension icon → {'"Fill this page"'}. See your Free/Pro status and remaining autofills.</p>
          </div>
        </div>
      </div>

      {/* Security */}
      <div className="mb-12 border-2 border-border p-6 bg-secondary/20">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 bg-primary text-primary-foreground flex items-center justify-center shrink-0">
            <Shield className="w-5 h-5" />
          </div>
          <div>
            <div className="font-heading text-sm uppercase tracking-widest mb-2">PRIVACY-FIRST BY DEFAULT</div>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Your profile lives on lifeautofill.com — the extension only reads it when you fill a form.</li>
              <li>• Unknown fields are matched by AI using only their labels (e.g. "Referee phone"). Your personal values never leave our servers for this.</li>
              <li>• Documents are only attached to upload boxes that clearly ask for them. Turn this off in the extension popup anytime.</li>
              <li>• Any field you fix by hand is saved to Form Memory, and you can delete it below at any time.</li>
              <li>• Passwords, card numbers, CVVs and PINs are never learned.</li>
              <li>• Free plan: 2 autofills per month. Pro: unlimited.</li>
              <li>• Sign out from the popup anytime to disconnect.</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Fallback */}
      <div className="text-center py-6 border-t border-border">
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">{"CAN'T INSTALL RIGHT NOW?"}</p>
        <Link to="/fill" className="inline-flex items-center gap-2 text-sm uppercase tracking-widest hover:text-primary" data-testid="fallback-fill-link">
          Use the click-to-copy Fill Helper <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
